import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { MapPin, Mountain, Waves, TreePine, Camera, Clock, Car, Compass, ArrowRight, Landmark, Coffee, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PlanMyTripButton } from '../components/TripPlannerQuiz';
import { SEOHead, BreadcrumbSchema, TouristAttractionSchema } from '../components/SEO';

const destinations = [
  {
    id: 'deomali',
    name: 'Deomali Peak',
    tagline: "Odisha's Highest Point",
    description: "Standing at 1,672 meters, Deomali is Odisha's highest peak. The sunrise views are legendary, painting the Eastern Ghats in hues of gold and crimson. The trek offers encounters with rare flora and fauna unique to this biodiversity hotspot.",
    image: 'https://images.unsplash.com/photo-1758683713567-2c958d1ff99b?w=1200&q=80',
    distance: '45 km',
    duration: '3-4 hours trek',
    bestTime: 'October to March',
    highlights: ['Sunrise Point', 'Rare Orchids', 'Bird Watching', 'Photography'],
    icon: Mountain,
  },
  {
    id: 'duduma',
    name: 'Duduma Waterfalls',
    tagline: 'The Niagara of Odisha',
    description: "Witness the majestic Machkund River plunging 175 meters down, creating a spectacle of nature's raw power. The surrounding forests are home to diverse wildlife, and the mist from the falls creates natural rainbows on sunny days.",
    image: 'https://images.pexels.com/photos/5675191/pexels-photo-5675191.jpeg?auto=compress&w=1200',
    distance: '80 km',
    duration: 'Full day trip',
    bestTime: 'Post-monsoon (Sept-Nov)',
    highlights: ['Viewing Platform', 'Hydroelectric Dam', 'Nature Walk', 'Picnic Spots'],
    icon: Waves,
  },
  {
    id: 'kolab',
    name: 'Kolab Reservoir',
    tagline: 'Serenity on Water',
    description: "One of the largest reservoirs in Odisha, Kolab offers pristine waters surrounded by lush forests. Perfect for boating, fishing, and simply unwinding by the serene lakeside while watching the sunset paint the sky.",
    image: 'https://images.pexels.com/photos/2887788/pexels-photo-2887788.jpeg?auto=compress&w=1200',
    distance: '35 km',
    duration: 'Half day',
    bestTime: 'Year-round',
    highlights: ['Boating', 'Fishing', 'Sunset Views', 'Bird Sanctuary'],
    icon: TreePine,
  },
  {
    id: 'jagannath',
    name: 'Jagannath Temple Koraput',
    tagline: 'Ancient Spiritual Heritage',
    description: "A 14th-century temple dedicated to Lord Jagannath, featuring stunning Odishan temple architecture. The annual Rath Yatra here is a grand celebration attracting devotees from across Odisha.",
    image: '/images/jagannath-temple-koraput.webp',
    distance: '40 km',
    duration: '2-3 hours',
    bestTime: 'Year-round, Rath Yatra festival',
    highlights: ['Ancient Architecture', 'Rath Yatra', 'Spiritual Experience', 'Photography'],
    icon: Landmark,
  },
  {
    id: 'tribal',
    name: 'Tribal Markets & Villages',
    tagline: 'Living Cultural Heritage',
    description: "Experience the rich cultural heritage of the Bonda, Gadaba, and Paraja tribes. Visit traditional weekly markets (haats), witness ancient Dhokra metal crafts, and learn about sustainable living practices passed down through generations.",
    image: 'https://images.pexels.com/photos/22820069/pexels-photo-22820069.jpeg?auto=compress&w=1200',
    distance: '20-50 km',
    duration: 'Half to full day',
    bestTime: 'Weekly haat days',
    highlights: ['Traditional Markets', 'Dhokra Craft', 'Tribal Dance', 'Local Cuisine'],
    icon: Compass,
  },
  {
    id: 'coffee',
    name: 'Koraput Coffee Plantations',
    tagline: 'Arabica in the Hills',
    description: "Koraput produces some of India's finest Arabica coffee. Tour the organic plantations nestled in misty hills, learn about sustainable farming, and sample freshly brewed coffee with panoramic valley views.",
    image: 'https://images.unsplash.com/photo-1611421447966-13d6876b3659?w=1200&q=80',
    distance: '15-30 km',
    duration: '2-3 hours',
    bestTime: 'November to February',
    highlights: ['Plantation Tours', 'Coffee Tasting', 'Organic Farming', 'Hill Views'],
    icon: Coffee,
  },
];

export default function ExplorePage() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://thehotelemerald.in/' },
    { name: 'Explore Koraput', url: 'https://thehotelemerald.in/explore' }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="explore-page">
      <SEOHead
        title="Explore Koraput | The Hotel Emerald - Tourist Attractions & Itineraries"
        description="Discover the wonders of Koraput - Deomali Peak, Duduma Falls, Kolab Reservoir, tribal heritage, and coffee plantations. Plan your Eastern Ghats adventure."
        keywords="koraput tourism, deomali peak, duduma falls, kolab reservoir, tribal villages odisha, eastern ghats attractions"
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <TouristAttractionSchema attractions={destinations} />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758683713567-2c958d1ff99b?w=1920&q=80"
            alt="Eastern Ghats at Sunset"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/50" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overline text-gold mb-3"
          >
            Discover
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-serif text-4xl md:text-5xl lg:text-6xl mb-4"
          >
            Explore Koraput
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-emerald-100 max-w-2xl mx-auto text-lg"
          >
            Journey through the untouched beauty of Odisha's Eastern Ghats
          </motion.p>
        </div>
      </section>

      {/* Destinations */}
      <section className="section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              Must-Visit Attractions
            </h2>
          </motion.div>

          <div className="space-y-24">
            {destinations.map((dest, index) => (
              <motion.div
                key={dest.id}
                id={dest.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? '' : ''}`}
                data-testid={`destination-${dest.id}`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="aspect-[4/3] rounded-sm overflow-hidden">
                    <img
                      src={dest.image}
                      alt={dest.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-gold flex items-center justify-center rounded-sm">
                    <dest.icon className="w-10 h-10 text-emerald-950" />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <p className="overline text-gold mb-2">{dest.tagline}</p>
                  <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">{dest.name}</h3>
                  <p className="text-muted-foreground mb-6 leading-relaxed">{dest.description}</p>

                  {/* Info Grid */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="flex items-center gap-2 text-sm">
                      <Car className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                      <span className="text-muted-foreground">{dest.distance}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                      <span className="text-muted-foreground">{dest.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Camera className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
                      <span className="text-muted-foreground">{dest.bestTime}</span>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2">
                    {dest.highlights.map((highlight) => (
                      <span key={highlight} className="text-xs bg-muted px-3 py-1 rounded-full">
                        {highlight}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="overline text-emerald-700 dark:text-emerald-400 mb-3">Location</p>
            <h2 className="font-serif text-3xl md:text-4xl text-foreground">
              Find Your Way
            </h2>
          </motion.div>

          <div className="rounded-sm overflow-hidden h-[400px] border border-border" data-testid="map-container">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d121726.77947553752!2d82.82!3d18.87!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a3ad9c6d8c3c7e1%3A0x9e8c7c7c7c7c7c7c!2sSemiliguda%2C%20Odisha!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Semiliguda Location Map"
            />
          </div>
        </div>
      </section>

      {/* Plan My Trip CTA */}
      <section className="section-padding bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-900 text-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-64 h-64 bg-gold/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gold/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-gold" />
              <span className="text-gold text-sm font-medium">AI-Powered Trip Planning</span>
            </div>
            
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl mb-4">
              Plan Your Perfect <span className="text-gold">Koraput Adventure</span>
            </h2>
            <p className="text-emerald-200 text-lg mb-8 max-w-2xl mx-auto">
              Tell us your preferences and our AI will create a personalized day-by-day itinerary 
              with the best attractions, dining spots, and experiences just for you.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <PlanMyTripButton 
                variant="hero" 
                className="text-base px-8 py-4"
              />
              <a 
                href="/emerald-ai" 
                className="btn-emerald bg-white/10 border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                Chat with Emerald AI
              </a>
            </div>

            {/* Features */}
            <div className="grid sm:grid-cols-3 gap-6 mt-12 text-sm">
              <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="w-5 h-5 text-gold" />
                </div>
                <h4 className="font-medium mb-1">Personalized Routes</h4>
                <p className="text-emerald-300 text-xs">Custom itinerary based on your interests and duration</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="w-5 h-5 text-gold" />
                </div>
                <h4 className="font-medium mb-1">Time-Optimized</h4>
                <p className="text-emerald-300 text-xs">Best times to visit each attraction for ideal experience</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 backdrop-blur-sm">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Camera className="w-5 h-5 text-gold" />
                </div>
                <h4 className="font-medium mb-1">Hidden Gems</h4>
                <p className="text-emerald-300 text-xs">Discover local secrets only known to experts</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
